import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getNotificationCount, getNotifications } from '../services/notifications';
import type { IQueryNotificationParams, NotificationType } from '../services/notifications/types';
import { NotificationScope } from '../services/notifications/types';
import { queryKeys } from './queryKeys';
import { STALE_TIME_NOTIFICATION_COUNT_MS } from './staleTimes';

function stableNotificationListParams(params?: IQueryNotificationParams): Record<string, unknown> {
  return {
    page: params?.page ?? 1,
    limit: params?.limit ?? 20,
    orderBy: params?.orderBy ?? 'createdAt',
    order: params?.order ?? 'DESC',
    scope: params?.scope,
    read: params?.read,
    type: params?.type,
  };
}

export async function fetchUnreadNotificationCount(type?: NotificationType): Promise<number> {
  try {
    const response = await getNotificationCount(type);
    return response >= 0 ? response : 0;
  } catch {
    const notificationsResponse = await getNotifications({
      read: false,
      limit: 1,
      page: 1,
      type,
      scope: NotificationScope.USER,
    });
    const realCount = notificationsResponse.meta.totalItems;
    return realCount >= 0 ? realCount : 0;
  }
}

export function useNotificationUnreadCountQuery(
  type?: NotificationType,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(type),
    queryFn: () => fetchUnreadNotificationCount(type),
    staleTime: STALE_TIME_NOTIFICATION_COUNT_MS,
    refetchInterval: options?.refetchInterval,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: options?.enabled ?? true,
  });
}

export function useNotificationsListQuery(
  params?: IQueryNotificationParams,
  options?: { enabled?: boolean }
) {
  const stableParams = stableNotificationListParams(params);

  return useQuery({
    queryKey: queryKeys.notifications.list(stableParams),
    queryFn: () => getNotifications(stableParams as IQueryNotificationParams),
    staleTime: STALE_TIME_NOTIFICATION_COUNT_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: options?.enabled ?? true,
  });
}

export function useInvalidateNotificationUnreadCount() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['notifications', 'count-unread'],
    });
  }, [queryClient]);
}

export function useInvalidateNotificationsCache() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['notifications', 'count-unread'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['notifications', 'list'],
    });
  }, [queryClient]);
}
